import { kebabCase } from 'lodash';
import React from 'react';
import { Icon, IconButton, Layout, Link, Typography } from '../../core';
import { i18n } from '../../i18n/translate';
import { formatClassName, pickDataAttributes } from '../../utils';
import { Button } from '../Button';
import { IconCategoryValueProps, LinkValue } from './types';
import { copyToClipboard } from './utils';
import { DesignSystem } from '@everfi/design-system';

export const IconCategoryValue = <C,>(props: IconCategoryValueProps<C>) => {
  const {
    category,
    className,
    copyValue,
    dataAttributesForEdit = {},
    editAction,
    icon,
    iconProps,
    linkValues,
    tooltip,
    tooltipPlacement = 'right',
    type = 'inline',
    url,
    value,
  } = props;

  const dataAtrName = value ? `${kebabCase(String(value))}-icon-category-value` : 'icon-category-value';

  const dataAttributes = pickDataAttributes(props, dataAtrName);

  const renderLinkValues = () => {
    if (!linkValues) return;

    const getNewWindowProps = (link: LinkValue): object => {
      if (link?.newWindow) return { target: '_blank', rel: 'noopener noreferrer' };

      return {};
    };

    if (Array.isArray(linkValues)) {
      return linkValues.map((link, index) => {
        const newWindowProps = getNewWindowProps(link);
        const isLastLink = index === linkValues.length - 1;

        return (
          <>
            <a href={link.url} {...newWindowProps}>
              {link.label ? link.label : link.url} {link.newWindow && <Icon iconName="arrow-up-right-from-square" />}
            </a>

            {!isLastLink && ', '}
          </>
        );
      });
    }

    const newWindowProps = getNewWindowProps(linkValues);

    return (
      <a href={linkValues.url} {...newWindowProps}>
        {linkValues.label ? linkValues.label : linkValues.url}
      </a>
    );
  };

  return (
    <Layout
      {...dataAttributes}
      className={formatClassName([className])}
      variant="flex"
      alignItems={'center'}
      columnGap={DesignSystem.Space['2XS']}
      rowGap={DesignSystem.Space.XS}
      sx={{
        p: {
          mb: '0',
        },
      }}
      textStyle="p2"
    >
      {/* @ts-ignore */}
      {icon && <Icon size="sm" iconName={icon} {...iconProps} />}

      <Layout variant="flex" gap={DesignSystem.Space.XS}>
        {category && (
          <Typography
            as="span"
            fontWeight="semibold"
            sx={{
              '&::after': {
                content: '":"',
              },
            }}
          >
            {category}
          </Typography>
        )}

        {type === 'stacked' && (
          // @ts-ignore
          <ButtonElements
            copyValue={copyValue}
            tooltip={tooltip}
            tooltipPlacement={tooltipPlacement}
            editAction={editAction}
            dataAttributesForEdit={dataAttributesForEdit}
            category={category}
            value={value}
          />
        )}

        <Layout className={formatClassName(['value', `${type}-value`])}>
          {url ? (
            <Link variant="neutral" href={url}>
              {value || url}
            </Link>
          ) : Array.isArray(value) ? (
            value.join(', ')
          ) : (
            value
          )}
          {linkValues && renderLinkValues()}

          {type === 'inline' && (
            // @ts-ignore
            <ButtonElements
              copyValue={copyValue}
              tooltip={tooltip}
              tooltipPlacement={tooltipPlacement}
              editAction={editAction}
              dataAttributesForEdit={dataAttributesForEdit}
              category={category}
              value={value}
            />
          )}
        </Layout>
      </Layout>
    </Layout>
  );
};

interface ButtonElementsProps<C> extends Pick<
  IconCategoryValueProps<C>,
  'value' | 'copyValue' | 'tooltip' | 'tooltipPlacement' | 'editAction' | 'category' | 'dataAttributesForEdit'
> {}

export const ButtonElements = <C,>(props: ButtonElementsProps<C>) => {
  const { copyValue, tooltip, tooltipPlacement, editAction, dataAttributesForEdit } = props;

  return (
    <>
      {copyValue && (
        <IconButton
          onClick={() => copyToClipboard(copyValue)}
          variant="link"
          paddingX={0}
          className="copy-button"
          aria-label={i18n.t('Copy')}
          icon={{ prefix: 'far', iconName: 'copy' }}
        />
      )}

      {tooltip && (
        <IconButton
          variant="link"
          colorScheme="info"
          className="info"
          icon={{ prefix: 'fas', iconName: 'info-circle' }}
          tooltip={{
            content: tooltip,
            placement: tooltipPlacement,
          }}
        />
      )}

      {editAction && (
        <Button
          {...dataAttributesForEdit}
          className="edit"
          style="link"
          icon="pen"
          label={i18n.t('Edit')}
          onClick={editAction}
          renderAsLegacy
        />
      )}
    </>
  );
};
